import {
  AutocompleteProps,
  Typography,
  CircularProgress,
  FormLabel,
  FormHelperText,
  FormControl,
  Skeleton,
  Box,
  Grid,
  createFilterOptions,
  BoxProps,
} from '@mui/joy'
import { useQuery } from '@tanstack/react-query'
import { ParseKeys } from 'i18next'
import { uniqBy } from 'lodash'
import { SyntheticEvent, useMemo, useState } from 'react'
import { Controller, ControllerProps } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useDebounce } from 'use-debounce'
import { Option } from '~/shared/config/constants'
import { useParserI18nMessage } from '~/shared/lib/useParserI18nMessage'
import { InternalChipList } from '~/shared/ui/Form/ui/InternalChipList'
import { SearchIcon } from '~/shared/ui/Icons'
import { ExternalChipList } from '../ui/ExternalChipList'
import { Listbox } from '../ui/Listbox'
import { Autocomplete } from '../ui/styled'

type MultipleType = 'external' | 'internal'

/* eslint-disable @typescript-eslint/no-explicit-any */
const filter = createFilterOptions<any>()

type AsyncAutocompleteInputProps<
  T extends Option,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined,
> = {
  name: string
  label?: ParseKeys
  placeholder?: ParseKeys
  skeletonShow?: boolean
  fetchOptions: (search: string) => Promise<T[]>
  queryKey?: unknown[]
  rules?: ControllerProps['rules']
  dataTestId?: string
  canAsyncSearch?: boolean
  autocompleteBoxSx?: BoxProps['sx']
} & Partial<AutocompleteProps<T, Multiple, DisableClearable, FreeSolo>> &
  (
    | {
        multiple: true
        autocompleteXs?: number
        multipleType?: MultipleType
      }
    | {
        multiple?: false | undefined
        autocompleteXs?: never
        multipleType?: never
      }
  )

export function AsyncAutocompleteInput<T extends Option>({
  name,
  label,
  placeholder,
  skeletonShow,
  fetchOptions,
  queryKey,
  multiple,
  autocompleteXs,
  multipleType = 'external',
  rules,
  dataTestId,
  canAsyncSearch = true,
  autocompleteBoxSx,
  ...props
}: AsyncAutocompleteInputProps<T, boolean, boolean, boolean>) {
  const { t } = useTranslation()
  const { parserI18nMessage } = useParserI18nMessage()

  const [isOpen, setIsOpen] = useState(false)
  const [search, setSearch] = useState<string>('')
  const [debouncedSearch] = useDebounce(canAsyncSearch ? search : '', 200)

  const { data, isError, isLoading, isFetching } = useQuery<T[]>(
    ['async-autocomplete-options', name, debouncedSearch, ...(queryKey || [])],
    () => fetchOptions(debouncedSearch),
    {
      keepPreviousData: false,
      retry: false,
      refetchOnWindowFocus: false,
    },
  )
  const loading = isLoading || isFetching || props.loading

  const options = useMemo(() => (data ? data : []), [data])

  const handleSearchSet = (
    _: SyntheticEvent,
    value: string,
    reason: 'input' | 'reset' | 'clear',
  ) => {
    if (reason === 'input') {
      setSearch(value)
    }
    if (!multiple && (reason === 'reset' || reason === 'clear')) {
      setSearch('')
    }
  }

  const handleOnBlur = () => {
    setIsOpen(false)
    setSearch('')
  }

  const handleOnFocus = () => {
    setIsOpen(true)
  }

  const noOptionsText = useMemo(() => {
    if (loading)
      return <Typography color='primary'>{t('alert.loading')}</Typography>
    if (isError)
      return <Typography color='danger'>{t('alert.server error')}</Typography>

    return (
      <Typography color='warning'>{t('alert.there are no options')}</Typography>
    )
  }, [loading, t, isError])

  const layout = useMemo(() => {
    if (multiple && multipleType === 'external') {
      return {
        formControl: {
          component: Grid,
          xs: 12,
        },
        autocompleteBox: {
          component: Grid,
          sx: {
            paddingTop: 0,
            paddingLeft: 0,
            paddingBottom: 0,
            ...autocompleteBoxSx,
          },
          xs: autocompleteXs,
        },
      }
    }
  }, [autocompleteBoxSx, autocompleteXs, multiple, multipleType])

  return (
    <Controller
      name={name}
      defaultValue={multiple ? [] : null}
      rules={rules}
      render={({
        field: { onChange, value },
        fieldState: { error, invalid },
      }) => (
        <FormControl error={invalid} {...layout?.formControl}>
          {label && <FormLabel>{t(label)}</FormLabel>}

          {multiple && multipleType === 'external' && (
            <ExternalChipList
              skeletonShow={skeletonShow}
              values={value || []}
              onChange={onChange}
              disabled={props?.readOnly}
            />
          )}

          <Box {...layout?.autocompleteBox}>
            <Autocomplete
              name={name}
              multiple={multiple}
              {...(multiple
                ? {
                    open: isOpen,
                    onBlur: handleOnBlur,
                    onFocus: handleOnFocus,
                    renderTags:
                      multipleType === 'internal'
                        ? (values: T[]) => (
                            <InternalChipList
                              values={values}
                              onChange={onChange}
                              disabled={props?.readOnly}
                            />
                          )
                        : () => null,
                  }
                : {})}
              size='lg'
              value={multiple ? value || [] : value || null}
              onChange={(_, value) => {
                onChange(value)
              }}
              filterOptions={(options, params) =>
                canAsyncSearch ? options : filter(options, params)
              }
              clearOnBlur
              onInputChange={handleSearchSet}
              {...(multiple && { inputValue: search })}
              options={
                !multiple &&
                value &&
                !options.some(({ id }) => id === value?.id)
                  ? uniqBy([value, ...options], 'id')
                  : options
              }
              noOptionsText={noOptionsText}
              startDecorator={!props.readOnly && <SearchIcon />}
              isOptionEqualToValue={(option, value) => option.id === value?.id}
              slotProps={{
                ...(skeletonShow ? { root: { component: Skeleton } } : {}),
                listbox: {
                  component: Listbox,
                  params: {
                    onClick: () => {
                      onChange(
                        uniqBy([...(value || []), ...(options || [])], 'id'),
                      )
                    },
                    isSelectAll: multiple,
                  },
                },
              }}
              {...(placeholder ? { placeholder: t(placeholder) } : {})}
              {...props}
              endDecorator={
                loading ? (
                  <CircularProgress data-testid='circular-progress' size='sm' />
                ) : (
                  props.endDecorator
                )
              }
            />
          </Box>

          {invalid && (
            <FormHelperText
              data-testid={`error-message-${dataTestId || (label && t(label))}`}
            >
              {parserI18nMessage(error?.message)}
            </FormHelperText>
          )}
        </FormControl>
      )}
    />
  )
}
